import React, {useEffect, useState} from "react";
import AppProfile from "./AppProfile";
import InstalledAppTile from "./InstalledAppTile";
import AvailableAppTile from "./AvailableAppTile";
import CoreAppTile from "./CoreAppTile";
import {
  AppLocationState,
  AvailableAppDetailsResponse,
  CoreAppDetailsResponse,
  GroupDetailResponse,
  InstalledAppDetailsResponse,
} from "../../../logic/APITypes";
import useAppDetails, {AppType} from "../../../hooks/useAppDetails";
import useAppDomain from "../../../hooks/useAppDomain";
import {validate as uuidValidate} from "uuid";
import {LoadingSkeleton} from "@spg/component_library";
import "./AppDetailsContainer.css";
import AppDomainTile from "./AppDomainTile";
import {useLocation, useParams} from "react-router-dom";
import {Resolution} from "../../../logic/FetchResolver";
import API from "../../../logic/API";
import {useIntl} from "react-intl";

export type AppDetailsResponse =
  | InstalledAppDetailsResponse
  | CoreAppDetailsResponse
  | AvailableAppDetailsResponse;

/** A wrapper for the AppDetails component taking care of what details to display */
function AppDetailsContainer({appType}: {appType: AppType}) {
  const {formatMessage} = useIntl();
  let {groupId, appId} = useParams();
  const {state}: {state: null | AppLocationState} = useLocation();
  const [group, setGroup] = useState<Resolution<GroupDetailResponse>>({
    success: false,
    unresolved: true,
  });
  const groupName = group.payload?.name ?? state?.groupName;
  const isGroupAdmin = group.payload?.is_admin ?? state?.isGroupAdmin;

  groupId = groupId || "";
  appId = appId || "";

  const {appDetails, loading} = useAppDetails(appType, groupId, appId);

  const {appDomain} =
    appType === "installed" || appType === "core"
      ? useAppDomain(appId)
      : {appDomain: undefined};

  useEffect(() => {
    if (
      !uuidValidate(appId) ||
      (appDetails && Object.keys(appDetails).length === 0)
    ) {
      window.location.href = "/apps";
    }
  }, [appId, appDetails]);

  useEffect(() => {
    if (!state?.groupName || state.isGroupAdmin === undefined)
      API.getGroup(groupId).then((response) => setGroup(response));
  }, []);

  if (loading || (!state?.groupName && group.unresolved)) {
    return (
      <div className="appDetails__skeleton">
        <LoadingSkeleton />
      </div>
    );
  }

  if (!appDetails) {
    return null;
  }

  const appUrls: {name: string; url: string}[] =
    "app_urls" in appDetails
      ? appDetails.app_urls
      : "app_url" in appDetails
        ? [{name: "App", url: appDetails.app_url}]
        : [];

  return (
    <div className="appDetails">
      <div className="appDetails__topSection">
        <AppProfile
          name={appDetails.name}
          description={appDetails.description}
          logo={appDetails.logo}
          appUrls={appUrls}
          groupId={groupId}
          groupName={
            appType === "core"
              ? formatMessage({id: "appDetailsContainer.core"})
              : (groupName ?? "")
          }
        />

        {appType === "installed" && (
          <InstalledAppTile
            appDetails={appDetails as InstalledAppDetailsResponse}
            groupId={groupId}
            isGroupAdmin={isGroupAdmin ?? false}
            appId={appId}
          />
        )}

        {appType === "available" && (
          <AvailableAppTile
            appDetails={appDetails as AvailableAppDetailsResponse}
            groupId={groupId}
            isGroupAdmin={isGroupAdmin ?? false}
            appId={appId}
          />
        )}

        {appType === "core" && (
          <CoreAppTile
            appDetails={appDetails as CoreAppDetailsResponse}
            appId={appId}
          />
        )}
      </div>

      {(appType === "core" || appType === "installed") && appDomain && (
        <AppDomainTile appDomain={appDomain} />
      )}
    </div>
  );
}

export default AppDetailsContainer;
